import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import { collection, limit, onSnapshot, orderBy, query, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../services/firebase/config';
import { useAuth } from '../../hooks/useAuth';
import { isAdminUser } from '../../utils/admin';

function formatWhen(value) {
  if (!value) return '';
  const date = value.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
}

export function AdminErrorNotifier() {
  const { currentUser } = useAuth();
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (!isAdminUser(currentUser)) return undefined;
    const errorsQuery = query(
      collection(db, 'errors'),
      orderBy('timestamp', 'desc'),
      limit(20)
    );
    let ready = false;
    const unsubscribe = onSnapshot(errorsQuery, (snapshot) => {
      if (!ready) {
        ready = true;
        return;
      }
      snapshot.docChanges().forEach((change) => {
        if (change.type !== 'added') return;
        const data = change.doc.data();
        setNotice(`${data.context || 'Error'}: ${data.message || 'Something went wrong'}`);
      });
    }, () => {});
    return unsubscribe;
  }, [currentUser]);

  if (!isAdminUser(currentUser)) return null;

  return (
    <Snackbar
      open={Boolean(notice)}
      autoHideDuration={8000}
      onClose={() => setNotice('')}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert severity="warning" onClose={() => setNotice('')} sx={{ width: '100%' }}>
        {notice}
      </Alert>
    </Snackbar>
  );
}

export function AdminErrorList() {
  const { currentUser } = useAuth();
  const [errors, setErrors] = useState([]);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!isAdminUser(currentUser)) return undefined;
    const errorsQuery = query(
      collection(db, 'errors'),
      orderBy('timestamp', 'desc'),
      limit(50)
    );
    return onSnapshot(errorsQuery, (snapshot) => {
      setErrors(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
      setLoadError('');
    }, (err) => {
      setLoadError(err.message || 'Could not load errors.');
    });
  }, [currentUser]);

  const acknowledge = async (id) => {
    await updateDoc(doc(db, 'errors', id), { acknowledged: true });
  };

  if (loadError) {
    return <Alert severity="error">{loadError}</Alert>;
  }

  if (errors.length === 0) {
    return <Typography color="text.secondary">No app errors logged yet.</Typography>;
  }

  return (
    <Stack spacing={2}>
      {errors.map((item) => (
        <Paper key={item.id} sx={{ p: 2 }} variant="outlined">
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Chip size="small" label={item.context || 'error'} />
            {item.errorCode && <Chip size="small" variant="outlined" label={item.errorCode} />}
            {!item.acknowledged && <Chip size="small" color="warning" label="New" />}
          </Stack>
          <Typography variant="body1">{item.message}</Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {formatWhen(item.timestamp)}
            {item.url ? ` · ${item.url}` : ''}
          </Typography>
          {!item.acknowledged && (
            <Box sx={{ mt: 1 }}>
              <Button size="small" onClick={() => acknowledge(item.id)}>Mark seen</Button>
            </Box>
          )}
        </Paper>
      ))}
    </Stack>
  );
}
